import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Drawer,
  Empty,
  Form,
  Descriptions,
  Input,
  Modal,
  List,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CopyableText from "components/common/CopyableText";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useAuth } from "contexts/AuthContext";
import olreadiaService from "services/olreadiaService";
import useBackgroundChecks from "hooks/olreadia/useBackgroundChecks";
import { useUserManagement } from "hooks";
import paths from "routes/paths";

const { Text } = Typography;

const BACKGROUND_CHECK_TYPES = [
  {
    value: "Profile validation and verification",
    label: "Profile validation and verification",
  },
  { value: "Criminal record checks", label: "Criminal record checks" },
];

const BACKGROUND_CHECK_STATUSES = [
  { value: "REQUESTED", label: "Requested" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "PASSED", label: "Passed" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELED", label: "Canceled" },
];

const getVerificationItemSnapshotSource = (record) =>
  record?.snapshot ??
  record?.currentItem ??
  record?.current_item ??
  record?.expectedSnapshot ??
  record?.expected_snapshot ??
  record?.valueSnapshot ??
  record?.value_snapshot ??
  null;

const getVerificationItemFoundValue = (record) =>
  record?.valueFound ??
  record?.value_found ??
  record?.foundValue ??
  record?.found_value ??
  record?.observedValue ??
  record?.observed_value ??
  record?.resultValue ??
  record?.result_value ??
  null;

const normalizeSnapshotPayload = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const toLabel = (value) =>
  value
    ? String(value)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (match) => match.toUpperCase())
    : "-";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
};

const getVerificationItemStatus = (record) => String(record?.status || "").toUpperCase();

const getVerificationItemStateLabel = (record) => {
  const status = getVerificationItemStatus(record);
  const foundValue = getVerificationItemFoundValue(record);
  if (status === "PASS" || status === "PASSED") return "Verified";
  if (status === "FAIL" || status === "FAILED") return "Failed";
  if (foundValue) return "Captured";
  return "Pending";
};

const getVerificationItemStateColor = (record) => {
  const status = getVerificationItemStatus(record);
  const foundValue = getVerificationItemFoundValue(record);
  if (status === "PASS" || status === "PASSED") return "green";
  if (status === "FAIL" || status === "FAILED") return "volcano";
  if (foundValue) return "blue";
  return "gold";
};

const getVerificationItemAccent = (record) => {
  const color = getVerificationItemStateColor(record);
  if (color === "green") {
    return {
      borderColor: "#22c55e",
      background: "#f0fdf4",
      shadow: "0 8px 22px rgba(34, 197, 94, 0.12)",
      softBackground: "#ecfdf5",
      softBorder: "#86efac",
    };
  }
  if (color === "volcano") {
    return {
      borderColor: "#f97316",
      background: "#fff7ed",
      shadow: "0 8px 22px rgba(249, 115, 22, 0.12)",
      softBackground: "#fff7ed",
      softBorder: "#fdba74",
    };
  }
  if (color === "blue") {
    return {
      borderColor: "#3b82f6",
      background: "#eff6ff",
      shadow: "0 8px 22px rgba(59, 130, 246, 0.12)",
      softBackground: "#eff6ff",
      softBorder: "#93c5fd",
    };
  }
  return {
    borderColor: "#f59e0b",
    background: "#fffbeb",
    shadow: "0 8px 22px rgba(245, 158, 11, 0.12)",
    softBackground: "#fff7d6",
    softBorder: "#fcd34d",
  };
};

const getVerificationItemKey = (record, index = null) =>
  [
    record?.propertyPath || "",
    record?.itemType || "",
    index !== null ? String(index) : "",
  ]
    .filter(Boolean)
    .join("|");

const buildVerificationItemFormValues = (record = null) => ({
  itemType: record?.itemType || null,
  propertyPath: record?.propertyPath || "",
  currentItem: normalizeSnapshotPayload(
    getVerificationItemSnapshotSource(record) || record?.currentItem || "",
  ),
  valueFound: getVerificationItemFoundValue(record) || "",
  matchResult: record?.matchResult,
  status: record?.status,
  required: typeof record?.required === "boolean" ? record.required : true,
});

const tryParseJsonValue = (value) => {
  if (value == null || value === "") return null;
  if (Array.isArray(value) || typeof value === "object") return value;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (
    !(
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    )
  ) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const formatSnapshotDateValue = (value) => {
  if (value == null || value === "") return "Not provided";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
};

const formatSnapshotPrimitiveValue = (value) => {
  if (value == null || value === "") return "Not provided";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const resolveSnapshotPathValue = (source, path) => {
  if (!source || !path) return null;

  const tokens = String(path)
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .map((token) => token.trim())
    .filter(Boolean);

  let current = source;
  for (const token of tokens) {
    if (current == null) return null;
    current = current[token];
  }

  return current;
};

const renderSnapshotFieldValue = (value) => {
  if (value == null || value === "") {
    return <Text type="secondary">Not provided</Text>;
  }
  if (typeof value === "boolean") {
    return <Tag>{value ? "Yes" : "No"}</Tag>;
  }
  if (typeof value === "object") {
    return <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(value, null, 2)}</Text>;
  }
  return <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(value)}</Text>;
};

const SNAPSHOT_SECTIONS = [
  {
    key: "identity",
    title: "Identity",
    fields: [
      ["Profile name", "profileName"],
      ["Profile type", "profileType"],
      ["Profile summary", "profileSummary"],
      ["User ID", "userId"],
      ["Profession profile ID", "id"],
    ],
  },
  {
    key: "classification",
    title: "Classification",
    fields: [
      ["Sector", "sector"],
      ["Sectors", "sectors"],
      ["Education level", "educationLevel"],
      ["Experience", "experience"],
      ["Previous regions", "previousRegionsWorkedIn"],
    ],
  },
  {
    key: "work",
    title: "Work Experience",
    arrayPath: "workExperienceHistory",
    emptyText: "No work experience entries",
  },
  {
    key: "education",
    title: "Education",
    arrayPath: "education",
    emptyText: "No education entries",
  },
  {
    key: "certifications",
    title: "Certifications",
    arrayPath: "certifications",
    emptyText: "No certifications",
  },
  {
    key: "documents",
    title: "Documents",
    arrayPath: "documents",
    emptyText: "No documents",
  },
  {
    key: "photos",
    title: "Photos",
    arrayPath: "photos",
    emptyText: "No photos",
  },
  {
    key: "references",
    title: "References",
    arrayPath: "references",
    emptyText: "No references",
  },
  {
    key: "serviceInterest",
    title: "Service Interest",
    arrayPath: "interestedServices",
    emptyText: "No service interests",
  },
  {
    key: "locationInterest",
    title: "Location Interest",
    arrayPath: "interestedLocations",
    emptyText: "No location interests",
  },
  {
    key: "shiftInterest",
    title: "Shift Interest",
    arrayPath: "interestedShifts",
    emptyText: "No shift interests",
  },
];

const renderSnapshotSectionFields = (source, fields) => {
  const visibleFields = fields
    .map(([label, path]) => [label, resolveSnapshotPathValue(source, path)])
    .filter(([, fieldValue]) => fieldValue !== undefined && fieldValue !== null && fieldValue !== "");

  if (!visibleFields.length) {
    return <Text type="secondary">No data in this section</Text>;
  }

  return (
    <Descriptions
      size="small"
      column={1}
      colon={false}
      labelStyle={{ width: 130, color: "#64748b", fontWeight: 600 }}
      contentStyle={{ color: "#0f172a" }}
    >
      {visibleFields.map(([label, fieldValue]) => (
        <Descriptions.Item key={label} label={label}>
          {renderSnapshotFieldValue(fieldValue)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};

const renderSnapshotSectionArray = (value) => {
  const parsed = tryParseJsonValue(value);
  if (!parsed) return <Text type="secondary">No data in this section</Text>;
  if (Array.isArray(parsed)) {
    if (!parsed.length) return <Text type="secondary">No entries found.</Text>;
    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {parsed.map((item, index) => renderSnapshotEntryCard(item, index))}
      </Space>
    );
  }
  if (typeof parsed === "object") {
    return renderSnapshotEntryCard(parsed, 0);
  }
  return renderSnapshotFieldValue(parsed);
};

const renderGroupedSnapshotValue = (value) => {
  const parsed = tryParseJsonValue(value);
  if (!parsed) {
    return <Text type="secondary">No snapshot data</Text>;
  }

  if (Array.isArray(parsed)) {
    return renderSnapshotSectionArray(parsed);
  }

  if (typeof parsed !== "object") {
    return renderSnapshotFieldValue(parsed);
  }

  const source = parsed;
  const sections = SNAPSHOT_SECTIONS.map((section) => {
    if (section.fields) {
      const hasData = section.fields.some(([, path]) => {
        const fieldValue = resolveSnapshotPathValue(source, path);
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
      });
      return {
        ...section,
        hasData,
      };
    }

    const sectionValue = resolveSnapshotPathValue(source, section.arrayPath);
    const parsedValue = tryParseJsonValue(sectionValue);
    const hasData = Array.isArray(parsedValue) ? parsedValue.length > 0 : Boolean(parsedValue);
    return {
      ...section,
      hasData,
      value: sectionValue,
    };
  }).filter((section) => section.hasData);

  if (!sections.length) {
    return <Text type="secondary">No snapshot data</Text>;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {sections.map((section) => (
        <Card
          key={section.key}
          size="small"
          style={{ borderRadius: 12, borderColor: "#e2e8f0" }}
          styles={{ body: { padding: 12 } }}
          title={section.title}
        >
          {section.fields
            ? renderSnapshotSectionFields(source, section.fields)
            : renderSnapshotSectionArray(section.value)}
        </Card>
      ))}
    </Space>
  );
};

const renderSnapshotEntryCard = (item, index) => {
  const record = item && typeof item === "object" ? item : { value: item };
  const title = record.organization || record.role || record.name || `Entry ${index + 1}`;
  const isWorkExperienceRow =
    Object.prototype.hasOwnProperty.call(record, "organization") ||
    Object.prototype.hasOwnProperty.call(record, "role") ||
    Object.prototype.hasOwnProperty.call(record, "location") ||
    Object.prototype.hasOwnProperty.call(record, "isCurrent");

  const fields = isWorkExperienceRow
    ? [
        ["Organization", record.organization],
        ["Role", record.role],
        ["Location", record.location],
        ["Start date", record.startDate],
        ["End date", record.endDate],
        ["Current", record.isCurrent],
        ["Notes", record.notes],
      ]
    : Object.entries(record);

  return (
    <Card
      key={`${title}-${index}`}
      size="small"
      styles={{ body: { padding: 12 } }}
      style={{ borderRadius: 12 }}
      title={
        <Space size={8} wrap>
          <Text strong>{title}</Text>
          {typeof record.isCurrent === "boolean" && record.isCurrent ? (
            <Tag>Current</Tag>
          ) : null}
        </Space>
      }
    >
      <Descriptions
        size="small"
        column={1}
        colon={false}
        labelStyle={{ width: 118, color: "#64748b", fontWeight: 600 }}
        contentStyle={{ color: "#0f172a" }}
      >
        {fields
          .filter(([, fieldValue]) => fieldValue !== undefined && fieldValue !== null && fieldValue !== "")
          .map(([label, fieldValue]) => (
            <Descriptions.Item key={label} label={label}>
              {label.toLowerCase().includes("date")
                ? formatSnapshotDateValue(fieldValue)
                : formatSnapshotPrimitiveValue(fieldValue)}
            </Descriptions.Item>
          ))}
      </Descriptions>
    </Card>
  );
};

const renderStructuredSnapshotValue = (value) => {
  return renderGroupedSnapshotValue(value);
};

const countChecksByStatus = (checks = []) => {
  const list = Array.isArray(checks) ? checks : [];
  const counts = {
    total: list.length,
    requested: 0,
    pending: 0,
    inProgress: 0,
    passed: 0,
    failed: 0,
    canceled: 0,
  };

  list.forEach((check) => {
    const status = String(check?.status || "").toUpperCase();
    if (status === "REQUESTED") counts.requested += 1;
    else if (status === "PENDING") counts.pending += 1;
    else if (status === "IN_PROGRESS") counts.inProgress += 1;
    else if (status === "PASSED") counts.passed += 1;
    else if (status === "FAILED") counts.failed += 1;
    else if (status === "CANCELED") counts.canceled += 1;
  });

  counts.open = counts.requested + counts.pending + counts.inProgress;
  counts.closed = counts.passed + counts.failed + counts.canceled;
  counts.passRate =
    counts.closed > 0 ? Math.round((counts.passed / counts.closed) * 100) : 0;
  return counts;
};

export default function BackgroundCheckDetailsPage() {
  const navigate = useNavigate();
  const { backgroundCheckId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const seededCheck = location.state?.check || null;
  const seededProfile = location.state?.profile || null;
  const seededHelperProfile = location.state?.helperProfile || location.state?.profile?.helperProfile || null;
  const seededUserEmail =
    seededHelperProfile?.email ||
    seededHelperProfile?.userEmail ||
    location.state?.check?.userEmail ||
    "";

  const { update, initiate, cancel, submitItemResult } = useBackgroundChecks();
  const { getUserById, searchUserProfiles } = useUserManagement(0, 20, null, true, { autoFetch: false });

  // Temporary diagnostics for empty detail screens.
  // eslint-disable-next-line no-console
  console.log("[BackgroundCheckDetails] route-state", {
    backgroundCheckId,
    hasCheckState: Boolean(location.state?.check),
    hasProfileState: Boolean(location.state?.profile),
    hasHelperProfileState: Boolean(location.state?.helperProfile || location.state?.profile?.helperProfile),
    seededCheckId: location.state?.check?.id || null,
    seededProfileId: location.state?.profile?.id || null,
    helperProfileId: location.state?.helperProfile?.id || location.state?.profile?.helperProfile?.id || null,
    helperProfileName:
      location.state?.helperProfile?.fullName ||
      [location.state?.helperProfile?.firstName, location.state?.helperProfile?.middleName, location.state?.helperProfile?.lastName]
        .filter(Boolean)
        .join(" ") ||
      location.state?.helperProfile?.userFullName ||
      null,
    helperProfileEmail:
      location.state?.helperProfile?.email ||
      location.state?.helperProfile?.userEmail ||
      null,
  });

  const [loading, setLoading] = useState(false);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [check, setCheck] = useState(seededCheck);
  const [profile, setProfile] = useState(seededProfile);
  const [activeUserDetails, setActiveUserDetails] = useState(null);
  const [error, setError] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemSubmitting, setItemSubmitting] = useState(false);
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [editForm] = Form.useForm();
  const [itemForm] = Form.useForm();

  const loadDetails = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!backgroundCheckId) return;
    // eslint-disable-next-line no-console
    console.log("[BackgroundCheckDetails] loadDetails:start", {
      backgroundCheckId,
      forceRefresh,
      hasSeededCheck: Boolean(seededCheck),
      hasSeededProfile: Boolean(seededProfile),
    });
    if (!forceRefresh && seededCheck && seededProfile) {
      // eslint-disable-next-line no-console
      console.log("[BackgroundCheckDetails] using seeded check/profile", {
        checkId: seededCheck?.id || null,
        profileId: seededProfile?.id || null,
      });
      setCheck(seededCheck || null);
      setProfile(seededProfile || null);
      setError(null);
      setLoading(false);
      if (seededCheck) {
        editForm.setFieldsValue({
          checkType: seededCheck?.checkType,
          status: seededCheck?.status,
          notes: seededCheck?.notes || "",
        });
      }
      return { check: seededCheck || null, profile: seededProfile || null };
    }
    setLoading(true);
    setError(null);
    try {
      const details = await olreadiaService.backgroundChecks.get(backgroundCheckId);
      // eslint-disable-next-line no-console
      console.log("[BackgroundCheckDetails] fetched background check", details);
      setCheck(details || null);
      editForm.setFieldsValue({
        checkType: details?.checkType,
        status: details?.status,
        notes: details?.notes || "",
      });

      const profileId =
        details?.professionProfileId || details?.professionProfile?.id;
      if (profileId) {
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] resolving profile", {
          profileId,
          seededProfileId: seededProfile?.id || null,
        });
        const profileRes =
          seededProfile?.id && String(seededProfile.id) === String(profileId)
            ? seededProfile
            : await olreadiaService.professionProfiles.get(profileId);
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] resolved profile", profileRes);
        setProfile(profileRes || null);
      } else {
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] no profileId on check; keeping seededProfile", seededProfile);
        setProfile(seededProfile);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BackgroundCheckDetails] loadDetails:error", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load background check details",
      );
      setCheck(null);
    } finally {
      setLoading(false);
    }
  }, [backgroundCheckId, editForm, seededCheck, seededProfile]);

  const syncDetails = useCallback(async () => {
    await loadDetails({ forceRefresh: true });
  }, [loadDetails]);

  useEffect(() => {
    if (seededCheck && seededProfile) return undefined;
    loadDetails();
    return undefined;
  }, [loadDetails, seededCheck, seededProfile]);

  const activeCheck = check;
  const checkStats = useMemo(
    () => countChecksByStatus(activeCheck ? [activeCheck] : []),
    [activeCheck],
  );
  const checkStatus = String(activeCheck?.status || "").toUpperCase();
  const isCheckInitiated = Boolean(activeCheck?.initiatedAt) || ["IN_PROGRESS", "PASSED", "FAILED", "CANCELED"].includes(checkStatus);
  const verificationItems = useMemo(
    () =>
      activeCheck?.verificationItems ||
      activeCheck?.verification_items ||
      activeCheck?.items ||
      [],
    [activeCheck?.verificationItems, activeCheck?.verification_items, activeCheck?.items],
  );
  const verificationStats = useMemo(() => {
    const items = Array.isArray(verificationItems) ? verificationItems : [];
    const completed = items.filter((item) => {
      const status = String(item?.status || "").toUpperCase();
      const foundValue = getVerificationItemFoundValue(item);
      return Boolean(
        foundValue ||
          status === "PASS" ||
          status === "FAIL" ||
          status === "PASSED" ||
          status === "FAILED",
      );
    }).length;
    const pending = Math.max(items.length - completed, 0);
    return {
      total: items.length,
      completed,
      pending,
      progress: items.length ? Math.round((completed / items.length) * 100) : 0,
    };
  }, [verificationItems]);
  const selectedVerificationItem = useMemo(() => {
    if (!selectedItemKey) return null;
    return (
      verificationItems.find(
        (item, index) => getVerificationItemKey(item, index) === selectedItemKey,
      ) || null
    );
  }, [selectedItemKey, verificationItems]);

  useEffect(() => {
    if (!verificationItems.length) {
      setSelectedItemKey(null);
      return;
    }

    const activeIndex = selectedItemKey
      ? verificationItems.findIndex((item, index) => getVerificationItemKey(item, index) === selectedItemKey)
      : -1;

    if (activeIndex === -1) {
      const firstItem = verificationItems[0];
      const firstKey = getVerificationItemKey(firstItem, 0);
      setSelectedItemKey(firstKey);
      itemForm.setFieldsValue(buildVerificationItemFormValues(firstItem));
      return;
    }

    const currentItem = verificationItems[activeIndex];
    itemForm.setFieldsValue(buildVerificationItemFormValues(currentItem));
  }, [itemForm, selectedItemKey, verificationItems]);

  const profileName = profile?.profileName || profile?.userFullName || "-";
  const profileGroup = profile?.profileType || "GENERAL";
  const sectorName =
    profile?.sector?.name ||
    profile?.sector?.code ||
    profile?.sectorCode ||
    "-";

  const checkUserName = activeUserDetails?.fullName || "-";
  const checkUserEmail = activeUserDetails?.email || "-";
  const checkUserPhone = activeUserDetails?.phone || "-";
  const isStatusEditable = [
    "IN_PROGRESS",
    "PASSED",
    "FAILED",
    "CANCELED",
  ].includes(checkStatus);

  // eslint-disable-next-line no-console
  console.log("[BackgroundCheckDetails] render", {
    activeCheckId: activeCheck?.id || null,
    profileId: profile?.id || null,
    profileName,
    checkUserName,
    checkUserEmail,
    checkUserPhone,
  });

  useEffect(() => {
    const activeUserId = activeCheck?.userId || activeCheck?.helperId || "";
    if (!activeUserId) {
      setActiveUserDetails(null);
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.log("[BackgroundCheckDetails] fetch user lookup", { activeUserId });

    let cancelled = false;

    const loadUserDetails = async () => {
      try {
        setUserDetailsLoading(true);

        if (seededUserEmail) {
          const emailMatches = await searchUserProfiles(seededUserEmail.trim());
          const exactEmailMatch = (emailMatches || []).find((entry) => {
            const entryEmail = String(entry?.email || entry?.userEmail || "").toLowerCase();
            return entryEmail && entryEmail === seededUserEmail.trim().toLowerCase();
          });
          if (exactEmailMatch && !cancelled) {
            setActiveUserDetails(exactEmailMatch);
          }
        }

        const details = await getUserById(activeUserId);
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] user lookup result", details);
        if (!cancelled) {
          setActiveUserDetails(details || null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[BackgroundCheckDetails] user lookup error", err);
        if (!cancelled) {
          setActiveUserDetails(null);
        }
      } finally {
        if (!cancelled) {
          setUserDetailsLoading(false);
        }
      }
    };

    loadUserDetails();

    return () => {
      cancelled = true;
    };
  }, [activeCheck?.helperId, activeCheck?.userId, getUserById, searchUserProfiles, seededUserEmail]);

  useEffect(() => {
    if (!activeCheck) return;
    editForm.setFieldsValue({
      checkType: activeCheck.checkType,
      status: activeCheck.status,
      notes: activeCheck.notes || "",
    });
  }, [activeCheck, editForm]);

  const openEditDrawer = useCallback(() => {
    if (!activeCheck) return;
    editForm.setFieldsValue({
      checkType: activeCheck.checkType,
      status: activeCheck.status,
      notes: activeCheck.notes || "",
    });
    setEditDrawerOpen(true);
  }, [activeCheck, editForm]);

  const closeEditDrawer = () => {
    setEditDrawerOpen(false);
  };

  const handleBack = () => {
    navigate(-1);
  };


  const handleGoToProfile = () => {
    if (!profile?.id) return;
    navigate(paths.helperProfessionProfileDetails(profile.id), {
      state: { profile },
    });
  };

  const handleSaveCheck = async () => {
    if (!activeCheck?.id) return;
    setSaving(true);
    try {
      const values = await editForm.validateFields();
      await update(
        activeCheck.id,
        {
          checkType: values.checkType,
          status: values.status,
          notes: values.notes || null,
        },
        actorId,
      );
      setEditDrawerOpen(false);
      await syncDetails();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to update background check");
    } finally {
      setSaving(false);
    }
  };

  const handleInitiateCheck = async () => {
    if (!activeCheck?.id) return;
    setSaving(true);
    try {
      await initiate(activeCheck.id, actorId);
      await syncDetails();
    } catch (err) {
      message.error(err?.message || "Failed to initiate background check");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelCheck = useCallback(() => {
    if (!activeCheck?.id) return;
    Modal.confirm({
      title: "Cancel background check?",
      content: "This will mark the background check as canceled.",
      okText: "Cancel check",
      okButtonProps: { danger: true },
      onOk: async () => {
        await cancel(activeCheck.id, actorId);
        await syncDetails();
      },
    });
  }, [activeCheck?.id, actorId, cancel, syncDetails]);

  const pageActionsMenu = useMemo(
    () => ({
      items: [
        {
          key: "edit",
          label: "Edit",
          onClick: openEditDrawer,
        },
        ...(String(activeCheck?.status || "").toUpperCase() !== "CANCELED"
          ? [
              {
                key: "cancel",
                label: "Cancel",
                danger: true,
                icon: <CloseOutlined />,
                onClick: handleCancelCheck,
              },
            ]
          : []),
      ],
    }),
    [activeCheck?.status, handleCancelCheck, openEditDrawer],
  );

  const openVerificationItem = (record = null, index = null) => {
    if (!isCheckInitiated) {
      message.info("Initiate the background check to unlock snapshots and verification items.");
      return;
    }
    const nextKey = getVerificationItemKey(record, index);
    setSelectedItemKey(nextKey);
    itemForm.setFieldsValue(buildVerificationItemFormValues(record));
  };

  const handleSaveItem = async () => {
    if (!activeCheck?.id) return;
    if (!selectedVerificationItem) {
      message.info("Select a verification item on the left first.");
      return;
    }
    setItemSubmitting(true);
    try {
      const values = await itemForm.validateFields();
      await submitItemResult(
        activeCheck.id,
        {
          itemType: selectedVerificationItem?.itemType || values.itemType || null,
          propertyPath: selectedVerificationItem?.propertyPath || values.propertyPath || null,
          currentItem:
            normalizeSnapshotPayload(
              getVerificationItemSnapshotSource(selectedVerificationItem) ||
                selectedVerificationItem?.currentItem ||
                values.currentItem ||
                null,
            ),
          valueFound: values.valueFound || null,
          matchResult: values.matchResult || null,
          status: values.status || null,
          required: Boolean(values.required),
        },
        actorId,
      );
      await syncDetails();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to save verification item");
    } finally {
      setItemSubmitting(false);
    }
  };

  if (loading || userDetailsLoading) {
    return (
      <div className="container-fluid p-2" style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div
          style={{
            minHeight: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-2" style={{ maxWidth: 1240, margin: "0 auto" }}>
      {error ? (
        <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }} styles={{ body: { padding: 12 } }}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text strong>Failed to load background check</Text>
            <Text type="secondary">{error}</Text>
            <div>
              <Button onClick={loadDetails}>Retry</Button>
            </div>
          </Space>
        </Card>
      ) : null}

      {activeCheck ? (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "2px 0 4px",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
              <WidgetSectionTitle
                icon="check"
                transform="shrink-2"
                title="Background Check"
                subtitle={activeCheck ? toLabel(activeCheck.checkType) : "Workflow details"}
                className="mb-0"
              />
            </div>
            <Space wrap size={8} style={{ justifyContent: "flex-end" }}>
              <Button onClick={handleBack}>
                Go Back
              </Button>
              {profile?.id ? (
                <Button onClick={handleGoToProfile}>
                  Profession Profile
                </Button>
              ) : null}
              <Dropdown menu={pageActionsMenu} trigger={["click"]}>
                <Button>
                  Actions <EllipsisOutlined />
                </Button>
              </Dropdown>
            </Space>
          </div>
          <Row gutter={[8, 8]}>
            <Col xs={24} lg={10}>
              <Card
                size="small"
                style={{ borderRadius: 10, height: "100%", borderColor: "#e5e7eb" }}
                styles={{ body: { padding: 10 } }}
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <div>
                    <Text style={{ fontSize: 13, fontWeight: 500, color: "#1f2937" }}>
                      {profileName}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
                        {profileGroup} {sectorName ? `• ${sectorName}` : ""}
                      </Text>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>
                        Name
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkUserName}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>
                        Email
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkUserEmail}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>
                        Phone
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkUserPhone}</Text>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>Status</Text>
                    <div style={{ marginTop: 6 }}>
                      <Space size={8} wrap align="center">
                        <Tag
                          style={{
                            marginInlineEnd: 0,
                            borderColor: "#d1d5db",
                            background: "#f9fafb",
                            fontWeight: 500,
                          }}
                        >
                          {toLabel(activeCheck.status)}
                        </Tag>
                        {isStatusEditable ? (
                          <Button
                            size="small"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={openEditDrawer}
                            style={{
                              color: "#2563eb",
                              background: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: 999,
                            }}
                            aria-label="Edit background check status"
                          />
                        ) : null}
                        {String(activeCheck.status || "").toUpperCase() === "REQUESTED" ||
                        String(activeCheck.status || "").toUpperCase() === "PENDING" ? (
                          <Button
                            size="small"
                            onClick={handleInitiateCheck}
                            disabled={saving}
                          >
                            {saving ? "Loading..." : "Initiate"}
                          </Button>
                        ) : null}
                      </Space>
                    </div>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>Flow</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkStats.closed} closed</Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card
                size="small"
                style={{ borderRadius: 10, height: "100%", borderColor: "#e5e7eb" }}
                styles={{ body: { padding: 10 } }}
              >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, letterSpacing: 0.2, fontWeight: 600 }}
                  >
                    Details
                  </Text>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>ID</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.id}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Type</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text strong style={{ fontSize: 12 }}>{toLabel(activeCheck.checkType)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Requested</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: 12 }}>{formatDateTime(activeCheck.requestedAt)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>By</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.requestedBy}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Started</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: 12 }}>{formatDateTime(activeCheck.initiatedAt)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Started by</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.initiatedBy}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Done</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: 12 }}>{formatDateTime(activeCheck.completedAt)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>User</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.userId}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Text type="secondary" style={{ fontSize: 10 }}>Notes</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                          {activeCheck.notes || "-"}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              flexWrap: "wrap",
              padding: "8px 0 0",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
              <WidgetSectionTitle
                icon="list"
                transform="shrink-2"
                title="Verification Workspace"
                subtitle="Pick a snapshot on the left, then compare it against your real-world verification evidence on the right."
                className="mb-0 mt-3"
              />
            </div>
            <Space wrap size={8} style={{ alignItems: "center" }}>
              <Tag>{verificationStats.total} items</Tag>
              <Tag>{verificationStats.pending} pending</Tag>
              <Tag>{verificationStats.completed} reviewed</Tag>
            </Space>
          </div>
          <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
            <Col xs={24}>
              <Card
                size="small"
                style={{
                  borderRadius: 14,
                  borderColor: "#e5e7eb",
                }}
                styles={{ body: { padding: 12 } }}
                title="Backend snapshots"
                extra={<Text type="secondary">{verificationStats.pending} pending</Text>}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {verificationItems.length} sections ready for review
                    </Text>
                    <Tag>{verificationStats.progress}% complete</Tag>
                  </div>

                  <Divider style={{ margin: "4px 0 0" }} />

                  {verificationItems.length ? (
                    <List
                      dataSource={verificationItems}
                      split={false}
                      renderItem={(record, index) => {
                        const key = getVerificationItemKey(record, index);
                        const isSelected = selectedItemKey === key;
                        const stateLabel = getVerificationItemStateLabel(record);
                        const accent = getVerificationItemAccent(record);
                        const isVerified = stateLabel === "Verified";
                        const rowStyle = isSelected
                          ? {
                              borderColor: accent.borderColor,
                              background: accent.background,
                              boxShadow: accent.shadow,
                            }
                          : isVerified
                          ? {
                              borderColor: accent.borderColor,
                              background: accent.softBackground,
                              boxShadow: `0 1px 2px ${accent.borderColor}22`,
                            }
                          : {
                              borderColor: "#e2e8f0",
                              background: "#ffffff",
                              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
                            };

                        return (
                          <List.Item style={{ padding: 0, marginBottom: 10 }}>
                            <Row gutter={12} style={{ width: "100%" }} align="stretch">
                              <Col xs={24} xl={8}>
                                <div
                                  style={{
                                    width: "100%",
                                    border: "1px solid",
                                    ...rowStyle,
                                    borderRadius: 14,
                                    padding: 12,
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => openVerificationItem(record, index)}
                                    disabled={!isCheckInitiated}
                                    style={{
                                      width: "100%",
                                      textAlign: "left",
                                      border: "none",
                                      background: "transparent",
                                      padding: 0,
                                      cursor: isCheckInitiated ? "pointer" : "not-allowed",
                                      opacity: isCheckInitiated ? 1 : 0.7,
                                    }}
                                  >
                                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                        <div style={{ minWidth: 0 }}>
                                          <Text strong style={{ fontSize: 13, color: "#0f172a" }}>
                                            {index + 1}. {toLabel(record?.itemType)}
                                          </Text>
                                          <div style={{ marginTop: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 11, wordBreak: "break-word" }}>
                                              {record?.propertyPath || "No property path"}
                                            </Text>
                                          </div>
                                        </div>
                                        <Space wrap size={6}>
                                          <Tag
                                            color={getVerificationItemStateColor(record)}
                                            icon={getVerificationItemStateLabel(record) === "Verified" ? <CheckOutlined /> : null}
                                          >
                                            {stateLabel}
                                          </Tag>
                                          {record?.required ? <Tag>Required</Tag> : <Tag>Optional</Tag>}
                                        </Space>
                                      </div>
                                    </Space>
                                  </button>

                                  {isSelected && isCheckInitiated ? (
                                    <div
                                      style={{
                                        marginTop: 12,
                                        borderRadius: 12,
                                        padding: 10,
                                        background: accent.softBackground,
                                        border: `1px solid ${accent.softBorder}`,
                                      }}
                                    >
                                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
                                        Backend snapshot
                                      </Text>
                                      <div style={{ marginTop: 6 }}>
                                        {renderStructuredSnapshotValue(
                                          getVerificationItemSnapshotSource(record),
                                        )}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </Col>

                              <Col xs={24} xl={16}>
                                {isSelected && isCheckInitiated ? (
                                  <Card
                                    size="small"
                                    style={{
                                      borderRadius: 14,
                                      borderColor: accent.borderColor,
                                      background: accent.background,
                                      boxShadow: accent.shadow,
                                    }}
                                    styles={{ body: { padding: 12, background: `linear-gradient(180deg, ${accent.background} 0%, #fff 100%)` } }}
                                    title="Capture result"
                                    extra={<Text type="secondary">Verify against calls, documents, or other evidence</Text>}
                                  >
                                    <Form layout="vertical" form={itemForm} initialValues={{ required: true }}>
                                      <Form.Item name="itemType" hidden>
                                        <Input />
                                      </Form.Item>
                                      <Form.Item name="propertyPath" hidden>
                                        <Input />
                                      </Form.Item>
                                      <Form.Item name="currentItem" hidden>
                                        <Input />
                                      </Form.Item>

                                      <Form.Item
                                        name="valueFound"
                                        label="Found value"
                                        extra="Enter what you verified from calls, documents, or other evidence."
                                      >
                                        <Input.TextArea
                                          placeholder="Observed value"
                                          autoSize={{ minRows: 4, maxRows: 8 }}
                                        />
                                      </Form.Item>

                                      <Row gutter={12}>
                                        <Col xs={24} md={12}>
                                          <Form.Item name="matchResult" label="Match">
                                            <Select
                                              allowClear
                                              placeholder="Select"
                                              options={[
                                                { value: "TOTAL_MATCH", label: "Total match" },
                                                { value: "PARTIAL_MATCH", label: "Partial match" },
                                                { value: "MISMATCH", label: "Mismatch" },
                                              ]}
                                            />
                                          </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                          <Form.Item name="status" label="Status">
                                            <Select
                                              allowClear
                                              placeholder="Select"
                                              options={[
                                                { value: "PASS", label: "Pass" },
                                                { value: "FAIL", label: "Fail" },
                                              ]}
                                            />
                                          </Form.Item>
                                        </Col>
                                      </Row>

                                      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                                        <Button onClick={handleSaveItem} loading={itemSubmitting} type="primary">
                                          Save
                                        </Button>
                                      </Space>
                                    </Form>
                                  </Card>
                                ) : (
                                  <div
                                    style={{
                                      minHeight: 64,
                                      borderRadius: 14,
                                      border: "1px dashed #e5e7eb",
                                      background: "#fff",
                                    }}
                                  />
                                )}
                              </Col>
                            </Row>
                          </List.Item>
                        );
                      }}
                      locale={{
                        emptyText: (
                          <Empty
                            description="No verification data is available for this check."
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <Empty
                      description="No verification data is available for this check."
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        </Space>
      ) : null}

      <Drawer
        open={editDrawerOpen}
        title="Edit check"
        width={720}
        zIndex={1500}
        onClose={closeEditDrawer}
        destroyOnHidden
        styles={{ body: { paddingBottom: 96 } }}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeEditDrawer}>Cancel</Button>
            <Button onClick={handleSaveCheck} loading={saving}>
              Save changes
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Space direction="vertical" size={4}>
              <Text type="secondary">Check</Text>
              <Text strong>{toLabel(activeCheck?.checkType)}</Text>
              <Text type="secondary">
                {profile?.profileName || profile?.userFullName || "-"}
              </Text>
            </Space>
          </Card>

          <Form layout="vertical" form={editForm}>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="checkType"
                  label="Check type"
                  rules={[
                    {
                      required: true,
                      message: "Select a background check type",
                    },
                  ]}
                >
                  <Select options={BACKGROUND_CHECK_TYPES} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: "Select a status" }]}
                >
                  <Select options={BACKGROUND_CHECK_STATUSES} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={3} placeholder="Optional admin notes" />
            </Form.Item>
          </Form>
        </Space>
      </Drawer>

    </div>
  );
}
